import { useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { Mail, CheckCircle2 } from "lucide-react";

export default function ForgotPassword() {
  const [identifier, setIdentifier] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setError("");
    try {
      await api.post("/auth/password-reset/request", { identifier: identifier.trim() });
      setSent(true);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  if (sent) {
    return (
      <div className="max-w-md mx-auto px-6 py-20">
        <div className="card-soft p-8 text-center" data-testid="reset-sent-card">
          <div className="w-14 h-14 rounded-full mx-auto flex items-center justify-center mb-4"
               style={{background:"rgba(91,117,83,0.12)"}}>
            <CheckCircle2 className="w-8 h-8" style={{color:"var(--brand)"}}/>
          </div>
          <h1 className="font-serif-display text-3xl">Check your inbox</h1>
          <p className="mt-3 text-sm" style={{color:"var(--text-secondary)"}}>
            If an account matches <strong>{identifier}</strong>, we've sent a reset link.
            Open your email and click the link within 2 hours. Don't see it? Check your spam folder.
          </p>
          <div className="mt-6 flex flex-col gap-2">
            <button data-testid="try-another" onClick={()=>{ setSent(false); setIdentifier(""); }}
                    className="btn-outline text-sm">
              Try another email
            </button>
            <Link to="/admin-login" className="text-sm underline" style={{color:"var(--brand)"}}>Back to sign in</Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-md mx-auto px-6 py-20">
      <h1 className="font-serif-display text-4xl mb-2">Forgot password?</h1>
      <p className="mb-8 text-sm" style={{color:"var(--text-secondary)"}}>
        Enter your email or username. We'll send a reset link to the email on file.
      </p>
      <form onSubmit={submit} className="space-y-4 card-soft p-8">
        <div>
          <label className="label-eyebrow block mb-2 inline-flex items-center gap-2">
            <Mail className="w-3.5 h-3.5"/> Email or username
          </label>
          <input data-testid="reset-identifier" type="text" required
            autoComplete="username"
            value={identifier} onChange={e=>setIdentifier(e.target.value)}
            placeholder="you@example.com"
            className="w-full rounded-xl border border-[color:var(--border-soft)] px-4 py-3 focus:outline-none focus:border-[color:var(--brand)] focus:ring-2 focus:ring-[color:var(--brand)]/30"/>
        </div>
        {error && <div data-testid="reset-error" className="text-sm text-[color:var(--accent)]">{error}</div>}
        <button data-testid="reset-submit" disabled={busy} className="btn-primary w-full">
          {busy ? "Sending…" : "Send reset link"}
        </button>
        <p className="text-sm text-center" style={{color:"var(--text-secondary)"}}>
          Remembered it? <Link data-testid="back-to-login" to="/admin-login" className="underline" style={{color:"var(--brand)"}}>Sign in</Link>
        </p>
      </form>
    </div>
  );
}
